import React, { useState, useEffect } from "react";
import {
  Type,
  FileText,
  List,
  CheckSquare,
  Circle,
  Sliders,
  Palette,
  Link,
  BarChart3,
  Copy,
  Trash2,
  Settings,
  Eye,
  EyeOff,
  ChevronDown,
} from "lucide-react";
import { QUESTION_TYPES } from "../models/types";
import { createOption } from "../utils/helpers";

const InlineQuestionEditor = ({
  question = null,
  onSave,
  onCancel,
  onDuplicate,
  onDelete,
  isPreview = false,
}) => {
  // Question types configuration with icons
  const questionTypesConfig = [
    {
      value: "text",
      label: "Short Answer",
      icon: Type,
      color: "text-blue-400",
    },
    {
      value: "textarea",
      label: "Paragraph",
      icon: FileText,
      color: "text-green-400",
    },
    {
      value: "select",
      label: "Dropdown",
      icon: List,
      color: "text-purple-400",
    },
    {
      value: "radio",
      label: "Multiple Choice",
      icon: Circle,
      color: "text-orange-400",
    },
    {
      value: "checkbox",
      label: "Checkboxes",
      icon: CheckSquare,
      color: "text-red-400",
    },
    {
      value: "multiselect",
      label: "Multi-Select",
      icon: List,
      color: "text-pink-400",
    },
    {
      value: "scale_1_10",
      label: "Linear Scale",
      icon: BarChart3,
      color: "text-indigo-400",
    },
    { value: "slider", label: "Slider", icon: Sliders, color: "text-teal-400" },
    {
      value: "color",
      label: "Color Picker",
      icon: Palette,
      color: "text-pink-400",
    },
    { value: "url", label: "Website URL", icon: Link, color: "text-cyan-400" },
  ];

  const [formData, setFormData] = useState({
    type: "text",
    title: "",
    description: "",
    placeholder: "",
    required: false,
    validation: null,
    defaultValue: "",
    options: [],
    leftLabel: "",
    rightLabel: "",
  });

  // State for custom dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".question-type-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  // Get current question type config
  const getCurrentQuestionType = () => {
    return (
      questionTypesConfig.find((qt) => qt.value === formData.type) ||
      questionTypesConfig[0]
    );
  };

  // Handle question type change
  const handleQuestionTypeChange = (newType) => {
    setFormData((prev) => ({
      ...prev,
      type: newType,
      // Reset options when changing to non-option types
      options: ["select", "radio", "checkbox", "multiselect"].includes(newType)
        ? prev.options
        : [],
    }));
    setIsDropdownOpen(false);
  };

  const [showPreview, setShowPreview] = useState(isPreview);
  const [isEditing, setIsEditing] = useState(!question);

  // Populate form with question data if editing
  useEffect(() => {
    if (question) {
      setFormData({
        type: question.type || "text",
        title: question.title || "",
        description: question.description || "",
        placeholder: question.placeholder || "",
        required: question.required || false,
        validation: question.validation || null,
        defaultValue: question.defaultValue || "",
        options: question.options || [],
        leftLabel: question.leftLabel || "",
        rightLabel: question.rightLabel || "",
      });
    }
  }, [question]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle option changes
  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...formData.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value,
    };

    setFormData({
      ...formData,
      options: updatedOptions,
    });
  };

  // Add a new option
  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, createOption("New Option")],
    });
  };

  // Remove an option
  const handleRemoveOption = (index) => {
    const updatedOptions = [...formData.options];
    updatedOptions.splice(index, 1);

    setFormData({
      ...formData,
      options: updatedOptions,
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setIsEditing(false);
  };

  // Determine if the question type requires options
  const hasOptions = ["select", "multiselect", "radio", "checkbox"].includes(
    formData.type
  );

  // Render question preview
  const renderPreview = () => {
    return (
      <div className="bg-neutral-800/60 backdrop-blur-sm border border-neutral-700 rounded-xl p-6">
        <div className="space-y-4">
          {/* Question Title */}
          <div>
            <label className="text-lg font-medium text-white">
              {formData.title || "Question Title"}
              {formData.required && (
                <span className="text-primary-green ml-1">*</span>
              )}
            </label>
            {formData.description && (
              <p className="text-sm text-neutral-400 mt-1">
                {formData.description}
              </p>
            )}
          </div>

          {/* Question Input Preview */}
          <div className="mt-2">
            {formData.type === QUESTION_TYPES.TEXT && (
              <input
                type="text"
                placeholder={formData.placeholder || "Your answer"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                disabled
              />
            )}

            {formData.type === QUESTION_TYPES.TEXTAREA && (
              <textarea
                placeholder={formData.placeholder || "Your answer"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white resize-none h-24"
                disabled
              />
            )}

            {formData.type === QUESTION_TYPES.SELECT && (
              <select
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white appearance-none"
                disabled
              >
                <option>Select an option</option>
                {formData.options.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {formData.type === QUESTION_TYPES.MULTISELECT && (
              <select
                multiple
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white appearance-none h-24"
                disabled
              >
                {formData.options.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {formData.type === QUESTION_TYPES.RADIO && (
              <div className="space-y-3">
                {formData.options.map((option, index) => (
                  <label key={index} className="flex items-center">
                    <input
                      type="radio"
                      name="preview"
                      className="mr-3 text-primary-green focus:ring-primary-green"
                      disabled
                    />
                    <span className="text-white">{option.label}</span>
                  </label>
                ))}
              </div>
            )}

            {formData.type === QUESTION_TYPES.CHECKBOX && (
              <div className="space-y-3">
                {formData.options.map((option, index) => (
                  <label key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-3 text-primary-green focus:ring-primary-green"
                      disabled
                    />
                    <span className="text-white">{option.label}</span>
                  </label>
                ))}
              </div>
            )}

            {formData.type === QUESTION_TYPES.SCALE_1_10 && (
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-neutral-400">
                  <span>{formData.leftLabel || "Low"}</span>
                  <span>{formData.rightLabel || "High"}</span>
                </div>
                <div className="flex space-x-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <label key={num} className="flex items-center">
                      <input
                        type="radio"
                        name="scale"
                        className="mr-2 text-primary-green focus:ring-primary-green"
                        disabled
                      />
                      <span className="text-sm text-white">{num}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.type === QUESTION_TYPES.SLIDER && (
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  className="w-full accent-primary-green"
                  disabled
                />
                <div className="flex justify-between text-sm text-neutral-400">
                  <span>0</span>
                  <span>100</span>
                </div>
              </div>
            )}

            {formData.type === QUESTION_TYPES.COLOR && (
              <input
                type="color"
                className="w-16 h-12 border border-neutral-700 rounded-lg bg-neutral-800"
                disabled
              />
            )}

            {formData.type === QUESTION_TYPES.URL && (
              <input
                type="url"
                placeholder={formData.placeholder || "https://example.com"}
                className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                disabled
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render editing form
  const renderEditForm = () => {
    return (
      <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-6">
        <div className="space-y-4">
          {/* Question Type Selector */}
          <div className="relative question-type-dropdown">
            <label className="block text-sm font-medium text-neutral-300 mb-2">
              Question Type
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white flex items-center justify-between"
              >
                <div className="flex items-center space-x-2">
                  {(() => {
                    const currentType = getCurrentQuestionType();
                    const Icon = currentType.icon;
                    return <Icon size={16} className={currentType.color} />;
                  })()}
                  <span>{getCurrentQuestionType().label}</span>
                </div>
                <ChevronDown size={16} className="text-neutral-400" />
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-neutral-800 border border-neutral-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {questionTypesConfig.map((questionType) => {
                    const Icon = questionType.icon;
                    return (
                      <button
                        key={questionType.value}
                        type="button"
                        onClick={() =>
                          handleQuestionTypeChange(questionType.value)
                        }
                        className={`w-full px-3 py-2 text-left hover:bg-neutral-700 flex items-center space-x-2 ${
                          formData.type === questionType.value
                            ? "bg-neutral-700/50"
                            : ""
                        }`}
                      >
                        <Icon size={16} className={questionType.color} />
                        <span className="text-white">{questionType.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Question Title */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-1">
              Question Text
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
              placeholder="Enter the question text"
              required
            />
          </div>

          {/* Question Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-1">
              Description / Help Text
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white h-20"
              placeholder="Optional description or help text"
            />
          </div>

          {/* Placeholder (for text inputs) */}
          {["text", "textarea", "url"].includes(formData.type) && (
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-1">
                Placeholder Text
              </label>
              <input
                type="text"
                name="placeholder"
                value={formData.placeholder}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                placeholder="Optional placeholder text"
              />
            </div>
          )}

          {/* Options (for select, multiselect, radio, checkbox) */}
          {hasOptions && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-neutral-300">
                  Options
                </label>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition"
                >
                  <span className="mr-1">+</span>
                  Add Option
                </button>
              </div>

              {formData.options.length === 0 ? (
                <div className="text-neutral-400 text-sm p-3 bg-neutral-800 border border-neutral-700 rounded-md">
                  No options defined. Click "Add Option" to create options.
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div
                      key={option.id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) =>
                          handleOptionChange(index, "label", e.target.value)
                        }
                        className="flex-1 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                        placeholder="Option text"
                      />
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) =>
                          handleOptionChange(index, "value", e.target.value)
                        }
                        className="w-1/3 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                        placeholder="Value (optional)"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                        title="Remove option"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Left and Right Labels for SCALE_1_10 */}
          {formData.type === QUESTION_TYPES.SCALE_1_10 && (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-300 mb-1">
                  Left Scale Label
                </label>
                <input
                  type="text"
                  name="leftLabel"
                  value={formData.leftLabel}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                  placeholder="e.g. Low, Disagree, etc."
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral-300 mb-1">
                  Right Scale Label
                </label>
                <input
                  type="text"
                  name="rightLabel"
                  value={formData.rightLabel}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
                  placeholder="e.g. High, Agree, etc."
                />
              </div>
            </div>
          )}

          {/* Required checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="required"
              id="required"
              checked={formData.required}
              onChange={handleChange}
              className="h-4 w-4 text-primary-green focus:ring-primary-green border-neutral-500 rounded"
            />
            <label
              htmlFor="required"
              className="ml-2 block text-sm text-neutral-300"
            >
              Required
            </label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-neutral-700 hover:bg-neutral-600 text-neutral-300 rounded-md transition"
          >
            {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
            <span>{showPreview ? "Edit" : "Preview"}</span>
          </button>

          {question && (
            <>
              <button
                onClick={() => onDuplicate && onDuplicate(question)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-md transition"
              >
                <Copy size={16} />
                <span>Duplicate</span>
              </button>

              <button
                onClick={() => onDelete && onDelete(question.id)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-md transition"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>

        {!showPreview && (
          <div className="flex space-x-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-neutral-600 text-neutral-300 rounded-md hover:bg-neutral-700 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-primary-green text-black rounded-md hover:bg-primary-green/90 transition"
              disabled={
                !formData.title || (hasOptions && formData.options.length === 0)
              }
            >
              {question ? "Update Question" : "Add Question"}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {showPreview ? renderPreview() : renderEditForm()}
    </div>
  );
};

export default InlineQuestionEditor;
